// --- DR.RX™ CORE APPLICATION TYPES ---
export type View = 
  | 'dashboard'
  | 'chat'
  | 'library'
  | 'all-tools'
  | 'account'
  | 'evidence-search'
  | 'drug-index'
  | 'drug-detail'
  | 'study-mode'
  | 'quiz'
  | 'clinical-guides'
  | 'pricing'
  | 'subscription-management'
  | 'admin-dashboard';

export type Platform = 'iOS' | 'Android' | 'Windows' | 'macOS' | 'Unknown';

// --- AUTH & USER ---
export interface User {
  name: string;
  email?: string;
}

export type SubscriptionPlan = 'Free' | 'Trial' | 'Plus' | 'Pro' | 'Teams';

export interface Subscription {
  plan: SubscriptionPlan;
  interactionsLeft: number;
  interactionLimit: number | 'unlimited';
  stripeId?: string;
  status?: string;
  role?: string;
}

export interface StripePrice {
  id: string;
  unit_amount: number;
  currency: string;
  interval: 'month' | 'year';
  description: string | null;
}

export interface StripeProduct {
  id: string;
  name: string;
  description: string;
  prices: StripePrice[];
  role: string;
  features: string[];
  active: boolean;
  popular?: boolean;
}


// --- CHAT ---
export enum ChatMessageRole {
  USER = 'user',
  MODEL = 'model',
}

export type MessageSource = 'local' | 'gemini';

export interface Message {
  id: string;
  text: string;
  role: ChatMessageRole;
  source?: MessageSource;
  blackBoxWarning?: string | null;
  action?: {
    type: 'ADD_DRUG' | 'RECOMMEND_TOOL';
    drugName?: string;
    toolName?: string;
    toolNames?: string[];
  };
  recommendedTools?: string[];
  addDrugName?: string;
  matchedLocalDrugId?: string;
  matchedLocalDrugName?: string;
  followUpPrompts?: string[];
  groundingMetadata?: any;
}

export interface ChatSession {
  id: string;
  title: string;
  messages: Message[];
  createdAt: number;
  isPinned?: boolean;
}

export type ProfessionalFocus = 'Pharmacist' | 'Physician' | 'Nurse' | 'Researcher';
export type LlmModel = 'gemini-3.8-flash' | 'gemini-2.5-flash' | 'gemini-flash-latest';
export type AnswerLength = 'short' | 'detailed';

export interface ChatSettings {
  professionalFocus: ProfessionalFocus;
  llmModel: LlmModel;
  answerLength?: AnswerLength;
}

// --- DRUGS & PHARMACY ---

export interface DosingInfo {
  indication: string;
  population: string;
  dose: string;
  notes?: string;
}

export interface AdverseReactionInfo {
  frequency: string;
  reactions: string;
}

export interface PharmacologyInfo {
  mechanismOfAction: string;
  pharmacokinetics?: {
    absorption: string;
    distribution: string;
    metabolism: string;
    excretion: string;
  };
}

export interface DrugInfo {
  id: string;
  brandName: string;
  genericName: string;
  therapeuticClass: string;
  dosageForm: string;
  strength: string;
  manufacturer: string;
  therapeuticEquivalence: string;
  isRx: boolean;
  isOtc: boolean;
  hasBlackBoxWarning: boolean;
  blackBoxWarning?: string | null;
  dosingAndAdministration?: string | DosingInfo[];
  adverseReactions?: string | AdverseReactionInfo[];
  contraindications?: string;
  drugInteractions?: string;
  pharmacology?: string | PharmacologyInfo;
  isUserAdded?: boolean;
}

// --- ALERTS ---
export interface Alert {
  id: string;
  source: string;
  severity: 'High' | 'Medium' | 'Low';
  title: string;
  summary: string;
  date: string; // ISO string
  link: string;
  details: string;
  state?: string;
  isGuestVisible?: boolean;
}

export interface FdaRecall {
    id: string;
    recallClass: 'Class I' | 'Class II' | 'Class III';
    drugName: string;
    manufacturer: string;
    recallDate: string;
    reason: string;
    ndc: string;
}

export interface CdcAdvisory {
    id: string;
    title: string;
    category: string;
    date: string;
    summary: string;
    link: string;
}

export interface DeaUpdate {
    id: string;
    title: string;
    category: string;
    date: string;
    summary: string;
    link: string;
}


// --- STUDY & QUIZ ---
export interface StudyDeck {
  id: string;
  name: string;
  drugIds: string[];
}

export type QuizMode = 'due' | 'missed' | 'bookmarked' | 'naplex';
export type QuizType = 'flashcard' | 'mcq';

export interface QuizSettings {
  mode: QuizMode;
  type: QuizType;
  drugIds: string[];
}

// --- DASHBOARD ---
export interface Activity {
  id: string;
  type: 'start_chat' | 'view_drug' | 'use_tool' | 'evidence_search';
  text: string;
  timestamp: number;
  payload: {
    sessionId?: string;
    drugId?: string;
    toolName?: string;
    query?: string;
  };
}

// --- GEMINI SERVICE & TOOLS ---
export interface EvidenceArticle {
    id: string;
    source: string;
    title: string;
    authors: string;
    publicationDate: string;
    summary: string;
    link: string;
}

export interface EvidenceSearchResult {
    synthesis: string;
    articles: EvidenceArticle[];
}

export interface MMEResult {
    totalDailyMme: number;
    riskAssessment: string;
    breakdown: { drug: string, mme: number }[];
}

export interface DoseAdjustmentResult {
    recommendation: string;
    notes: string;
}

export interface IVCompatibilityResult {
    status: 'Compatible' | 'Incompatible' | 'Variable' | 'Uncertain';
    notes: string;
}

export interface InteractionItem {
    drugs: string[];
    severity: 'Major' | 'Moderate' | 'Minor' | 'Unknown';
    mechanism: string;
    management: string;
}

export type DrugInteractionResult = InteractionItem[];

export interface CounselingPointsResult {
    counselingPoints: string;
}

export interface PillMatch {
    drugName: string;
    details: string;
    confidence: 'High' | 'Medium' | 'Low';
}
export interface PillIdentifierResult {
    matches: PillMatch[];
}

export interface AnticoagBridgingResult {
    preProcedurePlan: string;
    postProcedurePlan: string;
    notes: string;
}

export interface TherapeuticInterchangeResult {
    recommendation: string;
    notes: string;
}

export interface DaysSupplyResult {
    daysSupply: number;
    rationale: string;
}

export interface CompoundingCalcResult {
    formula: string;
    notes: string;
}

export interface LawResult {
    summary: string;
    reference: string;
}

export interface AntibioticInfoResult {
    spectrum: string;
    indications: string;
    dosing: string;
    adjustments: string;
}

export interface GuidelineSummaryResult {
    summary: string;
    keyRecommendations: string;
    sourceReference: string;
}

export interface DrugTaperingResult {
    schedule: string;
    notes: string;
}

export interface PediatricDoseResult {
    calculatedDose: string;
    dosePerKg: string;
    frequency: string;
    maxDose: string;
    notes: string;
}

export interface PharmacokineticsResult {
    creatinineClearance: number;
    interpretation: string;
    formulaUsed: string;
}

export interface AnticholinergicBurdenResult {
    totalScore: number;
    riskLevel: 'Low' | 'Moderate' | 'High' | 'Very High';
    breakdown: { drugName: string, score: number, rationale: string }[];
}

export interface InsulinConversionResult {
    recommendedDose: string;
    clinicalNotes: string;
}

export interface MissedDoseResult {
    guidance: string;
}

export interface VaccineRecommendation {
    vaccineName: string;
    rationale: string;
    category: 'Routinely Recommended' | 'Recommended Based on Medical Conditions' | 'Shared Clinical Decision-Making';
}
export type VaccineRecResult = VaccineRecommendation[];


export interface BeersCriteriaPIM {
    drugName: string;
    reason: string;
    alternatives: string;
}
export type BeersCriteriaResult = BeersCriteriaPIM[];


export interface AntidoteResult {
    antidote: string;
    dosing: string;
    notes: string;
}

export interface DrugShortageResult {
    status: 'On Shortage' | 'Limited Supply' | 'Available' | 'Unknown';
    lastUpdated: string;
    alternatives: string;
}

export interface LabValueResult {
    testName: string;
    normalRange: string;
    interpretationHigh: string;
    interpretationLow: string;
}

export interface DoseConversionResult {
    equivalentDose: string;
    notes: string;
}

export interface OTCProduct {
    productName: string;
    use: string;
    counselingPoints: string;
}
export type OTCResult = OTCProduct[];

export interface CSReferenceResult {
    schedule: string;
    federalSummary: string;
    stateSummary: string;
}

export interface DrugPriceResult {
    estimatedPriceRange: string;
    notes: string;
}

export interface PAJustificationResult {
    justificationText: string;
}

export interface SigCodeResult {
    translation: string;
    isSigToEnglish: boolean;
}

export interface EmergencyGuideResult {
    immediateActions: string;
    drugTherapy: string;
    monitoring: string;
}

export interface AnalyzeRxResult {
    sigTranslation: string;
    daysSupply: string;
    potentialIssues: { type: 'Clinical' | 'Legal' | 'Clarity', description: string }[];
}

export interface ClinicalTrial {
    nctNumber: string;
    title: string;
    status: string;
    phase: string;
    summary: string;
    link: string;
}
export type ClinicalTrialResult = ClinicalTrial[];

export interface DietaryGuideResult {
    withFood: string;
    foodsToAvoid: string;
    generalAdvice: string;
}

export interface FormularyResult {
    status: 'Covered' | 'Not Covered' | 'Prior Auth Required' | 'Non-Preferred' | 'Unknown';
    tier: string;
    alternatives: string;
}

export interface HivInfoResult {
    summary: string;
    details: { class: string, dosing: string, sideEffects: string, interactions: string };
}

export interface InrResult {
    recommendation: string;
    followUp: string;
}

export interface PgxResult {
    implication: string;
    recommendation: string;
}

export interface BiostatsResult {
    term: string;
    definition: string;
    example: string;
}

export interface GenericCalcResult {
    calculationName: string;
    formula: string;
    steps: string;
    result: string;
}

export interface CannabinoidInfoResult {
    name: string;
    mechanism: string;
    uses: string;
    adverseEffects: string;
    interactions: string;
    legalNote: string;
}

export interface ChemoToxicity {
    toxicity: string;
    system: 'Kidney' | 'Ear' | 'Heart' | 'Lung' | 'Nerves' | 'Bone Marrow' | 'Mouth' | 'General';
}
export type ChemoToxicityResult = ChemoToxicity[];

export interface CompoundingFormulaResult {
    name: string;
    disclaimer: string;
    ingredients: { name: string, quantity: string }[];
    instructions: string;
}

export interface MnemonicResult {
    topic: string;
    mnemonic: string;
    explanation: string;
}

export interface VitaminResult {
    name: string;
    functions: string;
    deficiencySigns: string;
    sources: string;
}

export interface PregnancyLactationResult {
    riskCategory: 'Contraindicated' | 'Use with Caution' | 'Compatible' | 'Unknown';
    pregnancySummary: string;
    lactationSummary: string;
    reproductivePotentialSummary: string;
}

export interface MedicationRelatedProblem {
    problem: string;
    category: 'Untreated Indication' | 'Drug Interaction' | 'Adverse Effect' | 'Suboptimal Dosing' | 'Unnecessary Therapy' | 'Adherence Concern' | 'Safety';
    evidence: string;
    recommendation: string;
}
export interface MTMResult {
    medicationRelatedProblems: MedicationRelatedProblem[];
    keyCounselingPoints: string[];
    monitoringParameters: string[];
}

// --- ADMIN ---

export interface MaintenanceConfig {
    isActive: boolean;
    message: string;
    type: 'info' | 'warning' | 'error';
}

export interface SecurityIssue {
    id: string;
    title: string;
    description: string;
    recommendation: string;
    severity: 'Critical' | 'High' | 'Medium' | 'Low' | 'Info';
    component: 'Frontend' | 'Backend' | 'Database';
}

export interface SecurityScanResult {
    score: number;
    issues: SecurityIssue[];
}

export interface AdminAnalytics {
    totalUsers: number;
    registeredUsers: number;
    anonymousUsers: number;
    activeSubs: number;
    totalChats: number;
    systemHealth: 'Healthy' | 'Degraded' | 'Down';
    diagnostics?: {
        apiKeyConfigured: boolean;
        ingestionModuleLoaded: boolean;
        geminiAdminModuleLoaded: boolean;
    };
}

export interface SystemLog {
    id: string;
    level: 'ERROR' | 'INFO' | 'WARN';
    message: string;
    source: string;
    timestamp: Date;
    context?: string;
}

export interface CEEntry {
    id: string;
    title: string;
    provider: string;
    date: string;
    credits: number;
}

export interface ClinicalPearl {
    id?: string;
    topic: string;
    content: string;
    category: 'Pharmacology' | 'Law' | 'Clinical Guideline' | 'Calculation' | 'General';
    source: string;
    sourceUrl?: string;
    status: 'DRAFT' | 'PUBLISHED' | 'SCHEDULED';
    scheduledDate: string;
}

export interface NewsTopic {
    headline: string; 
    source: string; 
    date: string; 
    summary: string; 
    url: string; 
}
